import ApplicationModel from 'components/framework/ApplicationModel';
import Fragment from 'components/framework/Fragment';
import {
  type RecordEvent,
  type RecordEventRaw,
} from 'components/framework/models/generated/types';
import type WorkflowStatus from 'components/framework/models/WorkflowStatus';
import type StatusRangeFilter from 'components/framework/filters/StatusRangeFilter';
import { orZero } from 'javascripts/util/orZero';
import type CycleTimeChartController from 'components/CycleTimeChart/controller/CycleTimeChartController';

type BaseEventParameters = {
  userId?: RecordId;
  endDate: string;
  subjectType?: string;
  projectId: RecordId;
};

type LoadEventParameters = BaseEventParameters & {
  startDate: string;
  statusRange: StatusRangeFilter;
  availableStatuses: WorkflowStatus[];
  controller: CycleTimeChartController;
};

type LoadEventV2Parameters = {
  startDate: string;
  endDate: string;
  projectId: RecordId;
  availableStatuses: WorkflowStatus[];
  controller: CycleTimeChartController;
};

type EventBatchParameters = BaseEventParameters & {
  subjectIds?: RecordId[];
  startDate?: string;
  statusIds?: string[];
  controller: CycleTimeChartController;
};

type FilterEventParams = {
  events: RecordEventRaw[];
  availableStatuses: WorkflowStatus[];
  statusRange: StatusRangeFilter;
  subjectType?: string;
  userId?: RecordId;
};

const EVENTS_PER_PAGE = 2000;

export function reworkEnabled(): boolean {
  return !!window.featureFlags.develop_1764_cycle_chart_rework;
}

// Loads all events for records that entered through the end state in the time range, then
// uses those to fetch all status entered events for the relevant records.
export async function loadCycleTimeEvents({
  statusRange,
  subjectType,
  userId,
  startDate,
  endDate,
  availableStatuses,
  projectId,
  controller,
}: LoadEventParameters): Promise<RecordEventRaw[]> {
  if (reworkEnabled()) {
    return loadCycleTimeEventsV2({
      projectId,
      startDate,
      endDate,
      availableStatuses,
      controller,
    });
  }

  const endEvents = await loadEventsInBatches({
    userId,
    projectId,
    subjectType,
    startDate,
    endDate,
    statusIds: [statusRange.end].filter(s => !!s) as string[],
    controller,
  });

  const groupedEvents = {};

  endEvents.forEach(event => {
    const type = event.eventSubjectType;

    if (groupedEvents[type] === undefined) groupedEvents[type] = [];

    groupedEvents[type].push(event);
  });

  const startEvents = await Promise.all(
    Object.keys(groupedEvents).map(type =>
      loadEventsInBatches({
        userId,
        endDate,
        projectId,
        statusIds: [statusRange.start].filter(s => !!s) as string[],
        subjectType: type,
        subjectIds: groupedEvents[type].map(event => event.eventSubject?.id),
        controller,
      })
    )
  );

  return startEvents
    .reduce((acc, elem) => acc.concat(elem), [])
    .concat(endEvents);
}

// Different event loader used with the rework - loads all for all statuses
// and filters client side - this is a slower initial load but faster when
// toggling filters.
async function loadCycleTimeEventsV2({
  startDate,
  endDate,
  availableStatuses,
  projectId,
  controller,
}: LoadEventV2Parameters): Promise<RecordEventRaw[]> {
  return loadEventsInBatches({
    projectId,
    startDate,
    endDate,
    statusIds: availableStatuses.map(s => s.id),
    controller,
  });
}

export function filterEvents({
  events,
  statusRange,
  availableStatuses,
  subjectType,
  userId,
}: FilterEventParams): RecordEventRaw[] {
  const startStatus: WorkflowStatus | undefined = availableStatuses.find(
    status => status.id === statusRange.start
  );

  return events.filter(event => {
    if (subjectType && event.eventSubjectType !== subjectType) return false;

    if (userId && event.assignedToUserId !== userId) return false;

    if (
      event.teamWorkflowStatus &&
      startStatus &&
      event.teamWorkflowStatus.position < startStatus.position
    )
      return false;

    return true;
  });
}

function eventFragment(
  filters,
  page: number,
  controller: CycleTimeChartController
): Fragment {
  const paginatedFilters = { ...filters, page: page, per: EVENTS_PER_PAGE };

  return new Fragment().fragment(
    new Fragment('recordEvents')
      .argument('filters', paginatedFilters, 'RecordEventFilters!')
      .fragment(
        new Fragment('raw')
          .attr(
            'createdAt',
            'assignedToUserId',
            'eventType',
            'eventSubjectType'
          )
          .fragment(
            ['Feature', 'Epic', 'Requirement'].reduce((fragment, type) => {
              let newFragment = new Fragment('', type)
                .union()
                .attr('id', 'name', 'referenceNum', 'path');

              // GraphQL requires args be present - only add them if they're not-nil
              if (controller.props.publishableId) {
                let drillInFragment = new Fragment('drillInUrl')
                  .argument('bookmarkId', controller.props.bookmarkId)
                  .argument('publishableId', controller.props.publishableId)
                  .argument(
                    'publishableType',
                    controller.props.publishableType
                  );

                if (controller.props.viewSnapshotId) {
                  drillInFragment = drillInFragment.argument(
                    'viewSnapshotId',
                    controller.props.viewSnapshotId
                  );
                }

                if (controller.props.dashboardId) {
                  drillInFragment = drillInFragment.argument(
                    'dashboardId',
                    controller.props.dashboardId
                  );
                }

                if (controller.props.exportType) {
                  drillInFragment = drillInFragment.argument(
                    'exportType',
                    controller.props.exportType
                  );
                }

                newFragment = newFragment.fragment(drillInFragment);
              }

              return fragment.fragment(
                newFragment.fragment(new Fragment('tags').attr('id', 'name'))
              );
            }, new Fragment('eventSubject'))
          )
          .fragment(
            new Fragment('teamWorkflowStatus').attr(
              'internalMeaning',
              'id',
              'position'
            )
          )
      )
  );
}

async function fetchEventPage(
  filters: object,
  page: number,
  controller: CycleTimeChartController
): Promise<{ raw: RecordEventRaw[]; rawPageCount: number }> {
  const { recordEvents } = await ApplicationModel.query<{
    recordEvents: RecordEvent;
  }>(eventFragment(filters, page, controller));

  return {
    raw: recordEvents.raw,
    rawPageCount: orZero(recordEvents.rawPageCount),
  };
}

// Loads raw events with the given filters, mimicking the batching behaviour in recordEvents.ts,
// but with a simpler fragment and no association injection.
async function loadEventsInBatches({
  userId,
  projectId,
  subjectIds,
  subjectType,
  startDate,
  endDate,
  statusIds,
  controller,
}: EventBatchParameters): Promise<RecordEventRaw[]> {
  const INITIAL_CONCURRENT_PAGES = 2;
  const MAX_CONCURRENT_PAGES = 3;

  let events: RecordEventRaw[] = [];
  let page = 1;
  let pagesLeft: number | null = null;

  const eventSubject = subjectType
    ? { typename: subjectType, id: subjectIds }
    : undefined;

  const filters = {
    eventSubject,
    eventType: [
      aha.enums.RecordEventTypeEnum.RECORD_ADDED_TO_TEAM_WORKFLOW_STATUS,
    ],
    teamId: projectId,
    assignedToUserId: userId,
    createdAt: {
      gt: startDate,
      lt: endDate,
    },
    teamWorkflowStatusId: statusIds,
  };

  while (pagesLeft === null || pagesLeft > 0) {
    const pagesToFetch =
      pagesLeft === null
        ? INITIAL_CONCURRENT_PAGES
        : Math.min(MAX_CONCURRENT_PAGES, pagesLeft);
    const results = await Promise.all(
      Array.from({ length: pagesToFetch }, (_, i) =>
        fetchEventPage(filters, page + i, controller)
      )
    );
    events = events.concat(...results.map(r => r.raw));
    if (pagesLeft === null) {
      pagesLeft = results[0].rawPageCount - 2;
    } else {
      pagesLeft -= pagesToFetch;
    }
    page += pagesToFetch;
  }

  return events;
}
